import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowsOut, CaretLeft, CaretRight, Play, Pause, Upload, Trash, Plus, Spinner, Eye } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../utils/api';

export default function Viewer360() {
  const [viewers, setViewers] = useState([]);
  const [activeViewer, setActiveViewer] = useState(null);
  const [viewerData, setViewerData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dragStartX = useRef(0);
  const dragStartIndex = useRef(0);
  const intervalRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchViewers = useCallback(async () => {
    try {
      const { data } = await api.get('/api/image-pro/360-viewers');
      setViewers(data);
    } catch {}
  }, []);

  useEffect(() => { fetchViewers(); }, [fetchViewers]);

  const loadViewer = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/api/image-pro/360-viewer/${id}`);
      setViewerData(data);
      setActiveViewer(id);
      setCurrentIndex(0);
      setIsPlaying(false);
    } catch { toast.error('Failed to load viewer'); }
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { data } = await api.post('/api/image-pro/360-viewer/create', new URLSearchParams({ title: `Vehicle 360° — ${new Date().toLocaleDateString()}` }));
      toast.success('360° viewer created');
      await fetchViewers();
      loadViewer(data.id);
    } catch { toast.error('Failed to create viewer'); }
    finally { setCreating(false); }
  };

  const handleUploadAngle = async (e) => {
    const files = e.target.files;
    if (!files?.length || !activeViewer) return;
    setUploading(true);
    let added = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        const formData = new FormData();
        formData.append('file', files[i]);
        formData.append('angle_label', `Angle ${(viewerData?.images?.length || 0) + i + 1}`);
        await api.post(`/api/image-pro/360-viewer/${activeViewer}/add-image`, formData);
        added++;
      } catch {}
    }
    if (added > 0) {
      toast.success(`${added} angle${added > 1 ? 's' : ''} added`);
      loadViewer(activeViewer);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/image-pro/360-viewer/${id}`);
      toast.success('Viewer deleted');
      if (activeViewer === id) { setActiveViewer(null); setViewerData(null); }
      fetchViewers();
    } catch { toast.error('Failed to delete'); }
  };

  // Auto-rotate
  useEffect(() => {
    if (isPlaying && viewerData?.images?.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % viewerData.images.length);
      }, 200);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isPlaying, viewerData]);

  // Drag-to-rotate
  const onPointerDown = useCallback((e) => {
    if (!viewerData?.images?.length) return;
    setIsDragging(true);
    setIsPlaying(false);
    dragStartX.current = e.clientX;
    dragStartIndex.current = currentIndex;
    e.preventDefault();
  }, [viewerData, currentIndex]);

  const onPointerMove = useCallback((e) => {
    if (!isDragging || !viewerData?.images?.length) return;
    const delta = e.clientX - dragStartX.current;
    const sensitivity = 5; // pixels per frame
    const indexDelta = Math.round(delta / sensitivity);
    const total = viewerData.images.length;
    const newIndex = ((dragStartIndex.current + indexDelta) % total + total) % total;
    setCurrentIndex(newIndex);
  }, [isDragging, viewerData]);

  const onPointerUp = useCallback(() => { setIsDragging(false); }, []);

  const images = viewerData?.images || [];
  const currentImage = images[currentIndex];

  return (
    <div className="space-y-5" data-testid="viewer-360">
      {/* Viewer List */}
      {!activeViewer && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-white">360° Vehicle Viewers</h3>
              <p className="text-xs text-slate-400">Create interactive spin presentations</p>
            </div>
            <Button onClick={handleCreate} disabled={creating}
              className="bg-cyan-500 text-black font-bold hover:bg-cyan-400 rounded-xl text-sm h-9 px-4 shadow-[0_0_15px_rgba(0,240,255,0.3)]"
              data-testid="create-360-btn">
              {creating ? <Spinner weight="bold" className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus weight="bold" className="w-4 h-4 mr-1.5" />}
              New 360° Viewer
            </Button>
          </div>

          {viewers.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-slate-700 rounded-2xl">
              <ArrowsOut weight="duotone" className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-lg font-semibold text-slate-400">No 360° viewers yet</p>
              <p className="text-sm text-slate-500 mt-1">Create one and upload vehicle angles</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {viewers.map(v => (
                <div key={v.id} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 hover:border-cyan-500/40 transition-all group" data-testid={`viewer-card-${v.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-white truncate">{v.title}</h4>
                    <button onClick={() => handleDelete(v.id)} className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-viewer-${v.id}`}>
                      <Trash weight="bold" className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">{v.image_count || 0} angles uploaded</p>
                  <Button onClick={() => loadViewer(v.id)} variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg text-xs h-8" data-testid={`open-viewer-${v.id}`}>
                    <Eye weight="bold" className="w-3.5 h-3.5 mr-1.5" />Open Viewer
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Active Viewer */}
      {activeViewer && viewerData && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => { setActiveViewer(null); setViewerData(null); }} className="text-slate-400 hover:text-white" data-testid="back-to-list">
                <CaretLeft weight="bold" className="w-5 h-5" />
              </button>
              <div>
                <h3 className="text-base font-bold text-white">{viewerData.title}</h3>
                <p className="text-xs text-slate-400">{images.length} angle{images.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="bg-slate-800 text-white border border-slate-700 hover:bg-slate-700 rounded-xl text-xs h-8 px-3"
                data-testid="upload-angle-btn">
                {uploading ? <Spinner weight="bold" className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload weight="bold" className="w-3.5 h-3.5 mr-1" />}
                Add Angles
              </Button>
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUploadAngle} data-testid="angle-file-input" />
            </div>
          </div>

          {images.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-slate-700 rounded-2xl">
              <Upload weight="duotone" className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-base font-semibold text-slate-400">Upload vehicle photos from different angles</p>
              <p className="text-sm text-slate-500 mt-1">8-24 angles recommended for smooth rotation</p>
              <Button onClick={() => fileInputRef.current?.click()} className="mt-4 bg-cyan-500 text-black font-bold hover:bg-cyan-400 rounded-xl text-sm" data-testid="upload-first-angle">
                <Upload weight="bold" className="w-4 h-4 mr-1.5" />Upload Photos
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Main Viewer */}
              <div
                className="relative rounded-xl overflow-hidden border border-white/10 bg-black cursor-grab active:cursor-grabbing select-none"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
                data-testid="spin-viewer"
              >
                <img
                  src={currentImage?.data_url}
                  alt={currentImage?.angle_label || `Angle ${currentIndex + 1}`}
                  className="w-full object-contain max-h-[55vh]"
                  draggable={false}
                />
                {/* Angle indicator */}
                <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  {currentImage?.angle_label || `${currentIndex + 1}/${images.length}`}
                </div>
                {/* Drag hint */}
                <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-slate-300 text-[10px] px-2.5 py-1 rounded-full flex items-center gap-1">
                  <ArrowsOut weight="bold" className="w-3 h-3" />Drag to rotate
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-3">
                <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white h-8 w-8"
                  onClick={() => setCurrentIndex(prev => (prev - 1 + images.length) % images.length)} data-testid="prev-angle">
                  <CaretLeft weight="bold" className="w-5 h-5" />
                </Button>
                <Button onClick={() => setIsPlaying(prev => !prev)}
                  className={`rounded-full w-10 h-10 ${isPlaying ? 'bg-cyan-500 text-black' : 'bg-slate-800 text-white border border-slate-700'}`}
                  data-testid="play-pause">
                  {isPlaying ? <Pause weight="fill" className="w-5 h-5" /> : <Play weight="fill" className="w-5 h-5" />}
                </Button>
                <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white h-8 w-8"
                  onClick={() => setCurrentIndex(prev => (prev + 1) % images.length)} data-testid="next-angle">
                  <CaretRight weight="bold" className="w-5 h-5" />
                </Button>
              </div>

              {/* Progress bar */}
              <div className="flex gap-0.5">
                {images.map((_, i) => (
                  <button key={i} onClick={() => { setCurrentIndex(i); setIsPlaying(false); }}
                    className={`flex-1 h-1 rounded-full transition-all ${i === currentIndex ? 'bg-cyan-500' : 'bg-slate-700 hover:bg-slate-600'}`}
                    data-testid={`angle-dot-${i}`} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
